import { STAT_TONES } from "@/lib/moduleTheme";
import { toDMY } from "@/lib/dates";

const rs = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const Card = ({ label, value, testId, tone = 0 }) => {
  const c = STAT_TONES[tone % STAT_TONES.length];
  return (
    <div
      data-testid={testId}
      className="rounded-sm border p-4"
      style={{ background: c.bg, borderColor: `${c.bar}40`, borderLeft: `3px solid ${c.bar}` }}
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: c.label }}>{label}</p>
      <p className="font-mono-num mt-1.5 text-lg font-semibold" style={{ color: c.value }}>{value}</p>
    </div>
  );
};

const SplitTable = ({ title, rows, testId }) => (
  <div className="border border-zinc-200 bg-white p-5">
    <h3 className="font-head text-base font-bold uppercase tracking-tight">{title}</h3>
    {rows?.length ? (
      <div className="mt-3 overflow-x-auto" data-testid={testId}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">
              <th className="py-1.5 text-left">Name</th>
              <th className="py-1.5 text-right">Sale</th>
              <th className="py-1.5 text-right">Cost</th>
              <th className="py-1.5 text-right">Hisab</th>
              <th className="py-1.5 text-right">Net profit</th>
              <th className="py-1.5 text-right">%</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((r) => (
              <tr key={r.label}>
                <td className="py-1.5 font-semibold uppercase">{r.label}</td>
                <td className="font-mono-num py-1.5 text-right">{rs(r.sale)}</td>
                <td className="font-mono-num py-1.5 text-right">{rs(r.cost)}</td>
                <td className="font-mono-num py-1.5 text-right">{rs(r.hisab_discount)}</td>
                <td className={`font-mono-num py-1.5 text-right font-semibold ${r.net_profit < 0 ? "text-red-600" : "text-emerald-700"}`}>
                  {rs(r.net_profit)}
                </td>
                <td className="font-mono-num py-1.5 text-right text-zinc-500">{r.margin_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <p className="mt-3 text-sm text-zinc-500">Nothing yet.</p>
    )}
  </div>
);

export const ProfitCards = ({ t, prefix = "profit" }) => (
  <div className="grid grid-cols-2 gap-4 lg:grid-cols-6" data-testid={`${prefix}-cards`}>
    <Card tone={1} testId={`${prefix}-sale`} label="Sale value (taxable)" value={rs(t?.sale)} />
    <Card tone={2} testId={`${prefix}-cost`} label="Cost (purchase rate)" value={rs(t?.cost)} />
    <Card tone={4} testId={`${prefix}-gross`} label="Gross profit" value={rs(t?.gross_profit)} />
    <Card tone={7} testId={`${prefix}-hisab`} label="Hisab discount" value={rs(t?.hisab_discount)} />
    <Card tone={Number(t?.net_profit) < 0 ? 3 : 1} testId={`${prefix}-net`} label="Net profit" value={rs(t?.net_profit)} />
    <Card tone={0} testId={`${prefix}-margin`} label="Profit %" value={`${t?.margin_pct ?? 0}%`} />
  </div>
);

export const ProfitPanel = ({ profit, title = "Profitability (after hisab discount)" }) => {
  if (!profit) return null;
  return (
    <section className="mt-8" data-testid="profit-panel">
      <h2 className="font-head text-lg font-bold uppercase tracking-tight">{title}</h2>
      <p className="mt-1 text-xs text-zinc-500">
        Net profit = taxable sale value − hisab extra discount − (meters × purchase rate from Item Master)
      </p>
      <div className="mt-4">
        <ProfitCards t={profit.totals} />
      </div>

      {profit.missing_purchase_rate?.length > 0 && (
        <p data-testid="profit-missing-rate" className="mt-3 border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          No purchase rate set in Item Master for: {profit.missing_purchase_rate.join(", ")}. Their cost counts as ₹0,
          so profit looks higher than it is.
        </p>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SplitTable title="Company wise" rows={profit.by_company} testId="profit-by-company" />
        <SplitTable title="Quality wise" rows={profit.by_quality} testId="profit-by-quality" />
      </div>

      <div className="mt-6 border border-zinc-200 bg-white p-5">
        <h3 className="font-head text-base font-bold uppercase tracking-tight">Bill wise profit</h3>
        {profit.bills?.length ? (
          <div className="mt-3 overflow-x-auto" data-testid="profit-bills">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400">
                  <th className="py-1.5 text-left">Bill</th>
                  <th className="py-1.5 text-left">Date</th>
                  <th className="py-1.5 text-right">Sale</th>
                  <th className="py-1.5 text-right">Cost</th>
                  <th className="py-1.5 text-right">Hisab</th>
                  <th className="py-1.5 text-right">Net profit</th>
                  <th className="py-1.5 text-right">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {profit.bills.map((b) => (
                  <tr key={b.id} data-testid={`profit-bill-${b.ref_no}`}>
                    <td className="py-1.5 font-semibold">{b.ref_no}</td>
                    <td className="py-1.5 text-zinc-500">{toDMY(b.date)}</td>
                    <td className="font-mono-num py-1.5 text-right">{rs(b.sale)}</td>
                    <td className="font-mono-num py-1.5 text-right">{rs(b.cost)}</td>
                    <td className="font-mono-num py-1.5 text-right">{rs(b.hisab_discount)}</td>
                    <td className={`font-mono-num py-1.5 text-right font-semibold ${b.net_profit < 0 ? "text-red-600" : "text-emerald-700"}`}>
                      {rs(b.net_profit)}
                    </td>
                    <td className="font-mono-num py-1.5 text-right text-zinc-500">{b.margin_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mt-3 text-sm text-zinc-500">No sales bills yet.</p>
        )}
      </div>
    </section>
  );
};
